import { notification, Pagination, Spin } from "antd";
import classNames from "classnames";
import cuid from "cuid";
import { get } from "lodash";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { BiCheck, BiChevronLeft } from "react-icons/bi";
import {
  MdClose,
  MdErrorOutline,
  MdOutlineCategory,
  MdOutlineCheckCircle,
  MdOutlineCloudUpload,
  MdOutlineDownload,
  MdOutlineError,
  MdOutlineExplore,
  MdOutlineInsertDriveFile,
  MdOutlineRefresh,
  MdOutlineWarning,
} from "react-icons/md";
import { useNavigate } from "react-router-dom";
import Button from "shared/components/Button";
import Header from "shared/components/Header";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { StoreContext } from "shared/store";
import { COLORS } from "shared/style/colors";
import { DELETE_ATTACHMENT } from "utils/api/graphql/mutations/attachment";
import {
  UPLOAD_INSURANCE_PLAN_DOCUMENTS,
  VALIDATE_INSURANCE_PLAN_DOCS_LINK,
  EXECUTE_INSURANCE_PLAN_DOCS_LINK,
} from "utils/api/graphql/mutations/insurance-plans";
import { ATTACHMENTS } from "utils/api/graphql/queries/attachments";
import { INSURANCE_PLANS } from "utils/api/graphql/queries/insurance-plans";
import {
  INITIAL_VALUES,
  INSURANCE_PLAN_ATTACHMENTS_TYPES,
  SCREENS,
} from "utils/constants";
import * as XLSX from "xlsx";
import { autoFitColumns } from "../constants";
import StepCard from "../StepCard";
import {
  ACCEPTED_FILE_EXTENSIONS,
  CSV_TEMPLATE_EXAMPLE_ROW,
  CSV_TEMPLATE_HEADERS,
  DOCS_IMPORT_STEPS,
  DOCUMENT_TYPE_KEYS,
  FILE_ACCEPT_STRING,
  formatDocsSampleValue,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_MB,
  MAX_PDF_SIZE_BYTES,
  MAX_PDF_SIZE_MB,
  PDF_ACCEPT_STRING,
  resolveError,
} from "./constants";

const EXISTING_ATTACHMENTS_PAGE_SIZE = 10;

const STEPS = [
  DOCS_IMPORT_STEPS.UPLOAD_FILES,
  DOCS_IMPORT_STEPS.UPLOAD_CSV,
  DOCS_IMPORT_STEPS.MAPPING,
  DOCS_IMPORT_STEPS.VALIDATION,
  DOCS_IMPORT_STEPS.ANALYSIS,
  DOCS_IMPORT_STEPS.LINKING,
  DOCS_IMPORT_STEPS.RESULT,
];

const DocsImportWizard = () => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.INSURANCE_PLAN);
  const DOCS_IMPORT = t("DOCS_IMPORT", { returnObjects: true });
  const FORM = t("FORM", { returnObjects: true });
  const DOCUMENT_TYPE_LABELS = get(FORM, "DOCUMENTS.TYPES", {});
  const navigate = useNavigate();
  const {
    user: {
      organization: { id: organizationId },
    },
  } = useContext(StoreContext);

  const [currentStep, setCurrentStep] = useState(0);

  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [defaultDocType, setDefaultDocType] = useState("");

  const [fileData, setFileData] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [mapping, setMapping] = useState({});
  const [linkResult, setLinkResult] = useState(null);
  const [confirmed, setConfirmed] = useState(false);

  const [dragOver, setDragOver] = useState(false);
  const [csvDragOver, setCsvDragOver] = useState(false);
  const [selectedCsvFile, setSelectedCsvFile] = useState(null);
  const [sizeError, setSizeError] = useState(false);
  const pdfInputRef = useRef(null);
  const csvInputRef = useRef(null);

  const [existingDocsSearch, setExistingDocsSearch] = useState("");
  const [existingDocsPage, setExistingDocsPage] = useState(1);

  const allowedTypes = Object.values(INSURANCE_PLAN_ATTACHMENTS_TYPES);

  const attachmentsQueryVars = {
    where: { organization: { id: organizationId } },
    whereNot: { identifier: null },
    isIn: { type: allowedTypes },
  };

  const { data: existingAttachmentsData, loading: existingAttachmentsLoading } =
    useQuery(ATTACHMENTS, { variables: attachmentsQueryVars });

  const dedupedExistingAttachments = useMemo(() => {
    const typeAllowList = Object.values(INSURANCE_PLAN_ATTACHMENTS_TYPES);
    const allDocs = get(existingAttachmentsData, "attachments.data", []);
    const byName = {};
    for (const doc of allDocs) {
      const name = get(doc, "name", "");
      const hasValidType = typeAllowList.includes(get(doc, "type"));
      const existing = byName[name];
      if (!existing) {
        byName[name] = doc;
      } else {
        const existingValid = typeAllowList.includes(get(existing, "type"));
        if (hasValidType && !existingValid) {
          byName[name] = doc;
        } else if (hasValidType === existingValid) {
          if (get(doc, "id", "") > get(existing, "id", "")) {
            byName[name] = doc;
          }
        }
      }
    }
    return Object.values(byName);
  }, [existingAttachmentsData]);

  const existingDocsSearchTrimmed = existingDocsSearch.trim().toLowerCase();

  const filteredExistingAttachments = useMemo(() => {
    if (!existingDocsSearchTrimmed) {
      return dedupedExistingAttachments;
    }
    return dedupedExistingAttachments.filter((att) => {
      const haystack = `${att.name || ""} ${att.filename || ""} ${att.type || ""}`;
      return haystack.toLowerCase().includes(existingDocsSearchTrimmed);
    });
  }, [dedupedExistingAttachments, existingDocsSearchTrimmed]);

  const paginatedExistingAttachments = useMemo(() => {
    const start = (existingDocsPage - 1) * EXISTING_ATTACHMENTS_PAGE_SIZE;
    return filteredExistingAttachments.slice(
      start,
      start + EXISTING_ATTACHMENTS_PAGE_SIZE,
    );
  }, [filteredExistingAttachments, existingDocsPage]);

  useEffect(() => {
    const pageCount = Math.max(
      1,
      Math.ceil(
        filteredExistingAttachments.length / EXISTING_ATTACHMENTS_PAGE_SIZE,
      ),
    );
    if (existingDocsPage > pageCount) {
      setExistingDocsPage(pageCount);
    }
  }, [filteredExistingAttachments.length, existingDocsPage]);

  const refetchExistingAttachments = [
    { query: ATTACHMENTS, variables: attachmentsQueryVars },
  ];

  const [deleteAttachment] = useMutation(DELETE_ATTACHMENT, {
    refetchQueries: refetchExistingAttachments,
  });

  const [uploadDocuments] = useMutation(UPLOAD_INSURANCE_PLAN_DOCUMENTS, {
    refetchQueries: refetchExistingAttachments,
  });

  const [validateLink, { loading: validating }] = useMutation(
    VALIDATE_INSURANCE_PLAN_DOCS_LINK,
  );

  const [executeLink] = useMutation(EXECUTE_INSURANCE_PLAN_DOCS_LINK, {
    refetchQueries: [
      {
        query: INSURANCE_PLANS,
        awaitRefetchQueries: true,
        variables: INITIAL_VALUES,
      },
    ],
  });

  const stepName = STEPS[currentStep];

  const stepLabels = STEPS.map((step) => {
    const labelMap = {
      [DOCS_IMPORT_STEPS.UPLOAD_FILES]: DOCS_IMPORT.STEPS.UPLOAD_FILES,
      [DOCS_IMPORT_STEPS.UPLOAD_CSV]: DOCS_IMPORT.STEPS.UPLOAD_CSV,
      [DOCS_IMPORT_STEPS.MAPPING]: DOCS_IMPORT.STEPS.MAPPING,
      [DOCS_IMPORT_STEPS.VALIDATION]: DOCS_IMPORT.STEPS.VALIDATION,
      [DOCS_IMPORT_STEPS.ANALYSIS]: DOCS_IMPORT.STEPS.ANALYSIS,
      [DOCS_IMPORT_STEPS.LINKING]: DOCS_IMPORT.STEPS.LINKING,
      [DOCS_IMPORT_STEPS.RESULT]: DOCS_IMPORT.STEPS.RESULT,
    };
    return labelMap[step] || step;
  });

  const visibleStepIndex = STEPS.indexOf(stepName);

  const typeOptions = DOCUMENT_TYPE_KEYS.map((key) => ({
    label: get(DOCUMENT_TYPE_LABELS, key, key),
    value: key,
  }));

  const downloadTemplate = useCallback(() => {
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet([
      CSV_TEMPLATE_HEADERS,
      CSV_TEMPLATE_EXAMPLE_ROW,
    ]);
    autoFitColumns(worksheet, CSV_TEMPLATE_HEADERS);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Documents");
    XLSX.writeFile(workbook, "insurance-plans-docs-template.xlsx");
  }, []);

  const handlePdfFiles = (fileList) => {
    const newFiles = [];
    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      if (file.size > MAX_PDF_SIZE_BYTES) continue;
      const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
      if (ext !== ".pdf") continue;
      newFiles.push({
        id: cuid(),
        file,
        name: file.name,
        size: file.size,
        type: defaultDocType || "",
        status: "pending",
      });
    }
    setPendingFiles((prev) => [...prev, ...newFiles]);
  };

  const handleDefaultDocTypeChange = (docType) => {
    setDefaultDocType(docType);
    setPendingFiles((prev) =>
      prev.map((f) => (f.status !== "uploaded" ? { ...f, type: docType } : f)),
    );
  };

  const handlePdfTypeChange = (fileId, docType) => {
    setPendingFiles((prev) =>
      prev.map((f) => (f.id === fileId ? { ...f, type: docType } : f)),
    );
  };

  const handlePdfNameChange = (fileId, newName) => {
    setPendingFiles((prev) =>
      prev.map((f) => (f.id === fileId ? { ...f, name: newName } : f)),
    );
  };

  const handleRemoveFile = (fileId) => {
    setPendingFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  const handleDeleteExistingAttachment = async (attachmentId) => {
    try {
      await deleteAttachment({
        variables: { where: { id: attachmentId } },
      });
      notification.success({
        message: DOCS_IMPORT.UPLOAD_FILES.DELETE_SUCCESS,
      });
    } catch (error) {
      notification.error({
        message: DOCS_IMPORT.UPLOAD_FILES.DELETE_ERROR,
        description: error.message,
      });
    }
  };

  const readFileAsBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleUploadAll = async () => {
    const filesToUpload = pendingFiles.filter((f) => f.type && f.name.trim());
    if (filesToUpload.length === 0) return;

    setUploading(true);
    try {
      const filesPayload = [];
      for (const pf of filesToUpload) {
        const base64 = await readFileAsBase64(pf.file);
        filesPayload.push({
          base64,
          filename: pf.name,
          type: pf.type,
        });
      }

      const { data } = await uploadDocuments({
        variables: { data: { files: filesPayload } },
      });

      const result = get(data, "uploadInsurancePlanDocuments");
      setUploadResult(result);

      const uploadedFiles = get(result, "files", []);
      const successNames = uploadedFiles
        .filter((f) => f.success)
        .map((f) => f.filename);
      const failedFiles = uploadedFiles.filter((f) => !f.success);

      setPendingFiles((prev) =>
        prev.map((f) => ({
          ...f,
          status: successNames.includes(f.name) ? "uploaded" : f.status,
        })),
      );

      if (successNames.length > 0) {
        notification.success({
          message: `${successNames.length} / ${filesToUpload.length} ${DOCS_IMPORT.UPLOAD_FILES.TITLE}`,
        });
      }

      if (failedFiles.length > 0) {
        notification.error({
          message: `${failedFiles.length} ${DOCS_IMPORT.ERRORS.UPLOAD_FAILED}`,
          description: failedFiles
            .map((f) => `${f.filename}: ${f.error}`)
            .join(", "),
        });
      }
    } catch (error) {
      notification.error({
        message: DOCS_IMPORT.ERRORS.UPLOAD_FAILED,
        description: error.message,
      });
    } finally {
      setUploading(false);
    }
  };

  const handleContinueToLink = () => {
    setCurrentStep(STEPS.indexOf(DOCS_IMPORT_STEPS.UPLOAD_CSV));
  };

  const handleCsvFile = (file) => {
    setSizeError(false);
    const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
    if (!ACCEPTED_FILE_EXTENSIONS.includes(ext)) return;
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setSizeError(true);
      return;
    }
    setSelectedCsvFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      handleCsvUpload(reader.result, file.name);
    };
    reader.readAsDataURL(file);
  };

  const handleCsvUpload = async (base64, filename) => {
    setFileData({ base64, filename });
    try {
      const { data } = await validateLink({
        variables: { data: { base64, filename } },
      });
      const result = get(data, "validateInsurancePlanDocsLink");
      setValidationResult(result);
      setMapping(result.autoMapping || result.mapping || {});
      setCurrentStep(STEPS.indexOf(DOCS_IMPORT_STEPS.MAPPING));
    } catch (error) {
      notification.error({
        message: DOCS_IMPORT.ERRORS.VALIDATION_FAILED,
        description: error.message,
      });
    }
  };

  const handleMappingConfirm = async (updatedMapping) => {
    setMapping(updatedMapping);
    try {
      const { data } = await validateLink({
        variables: {
          data: {
            base64: fileData.base64,
            filename: fileData.filename,
            mapping: updatedMapping,
          },
        },
      });
      const result = get(data, "validateInsurancePlanDocsLink");
      setValidationResult(result);
      setCurrentStep(STEPS.indexOf(DOCS_IMPORT_STEPS.VALIDATION));
    } catch (error) {
      notification.error({
        message: DOCS_IMPORT.ERRORS.VALIDATION_FAILED,
        description: error.message,
      });
    }
  };

  const handleValidationNext = () => {
    setCurrentStep(STEPS.indexOf(DOCS_IMPORT_STEPS.ANALYSIS));
  };

  const handleExecuteLink = async () => {
    setCurrentStep(STEPS.indexOf(DOCS_IMPORT_STEPS.LINKING));
    try {
      const { data } = await executeLink({
        variables: {
          data: {
            base64: fileData.base64,
            filename: fileData.filename,
            mapping,
            strategy: "all",
          },
        },
      });
      const result = get(data, "executeInsurancePlanDocsLink");
      if (result) {
        setLinkResult(result);
      } else {
        setLinkResult({
          success: false,
          errors: [{ key: "UNEXPECTED_ERROR", params: {} }],
        });
      }
      setCurrentStep(STEPS.indexOf(DOCS_IMPORT_STEPS.RESULT));
    } catch (error) {
      const gqlMsg = get(error, "graphQLErrors[0].message", "");
      const msg = gqlMsg || error.message || "";
      let parsedError;
      try {
        parsedError = JSON.parse(msg);
      } catch (_e) {
        parsedError = null;
      }
      const errorObj =
        parsedError && parsedError.key
          ? parsedError
          : { key: "UNEXPECTED_ERROR", params: { message: msg } };
      setLinkResult({
        success: false,
        errors: [errorObj],
      });
      setCurrentStep(STEPS.indexOf(DOCS_IMPORT_STEPS.RESULT));
    }
  };

  const handleFinish = () => {
    navigate(SCREENS.PRIVATE.ADMIN.INSURANCE_PLANS.LIST.path);
  };

  const handleReset = () => {
    setCurrentStep(0);
    setPendingFiles([]);
    setUploadResult(null);
    setUploading(false);
    setDefaultDocType("");
    setFileData(null);
    setValidationResult(null);
    setMapping({});
    setLinkResult(null);
    setConfirmed(false);
    setSelectedCsvFile(null);
    setSizeError(false);
    setDragOver(false);
    setCsvDragOver(false);
  };

  const goBack = () => {
    if (currentStep === 0) {
      navigate(SCREENS.PRIVATE.ADMIN.INSURANCE_PLANS.LIST.path);
      return;
    }
    const currentVisible = STEPS.indexOf(stepName);
    if (currentVisible > 0) {
      const prevStep = STEPS[currentVisible - 1];
      setCurrentStep(STEPS.indexOf(prevStep));
    }
  };

  const renderUploadFiles = () => {
    const handleDrop = (e) => {
      e.preventDefault();
      setDragOver(false);
      handlePdfFiles(e.dataTransfer.files);
    };

    const filesReady = pendingFiles.filter((f) => f.type && f.name.trim());
    const hasIncomplete = pendingFiles.some((f) => !f.name.trim() || !f.type);
    const allUploaded =
      uploadResult && uploadResult.success && filesReady.length > 0;
    const hasExistingDocs = dedupedExistingAttachments.length > 0;
    const canSkipUpload = hasExistingDocs && pendingFiles.length === 0;

    const uploadActions = (
      <div className="d-flex gap-12">
        {canSkipUpload && !allUploaded && (
          <Button type="primary" onClick={handleContinueToLink}>
            <BiCheck size={20} />
            {DOCS_IMPORT.UPLOAD_FILES.SKIP_UPLOAD}
          </Button>
        )}
        {!allUploaded && pendingFiles.length > 0 && (
          <Button
            type="primary"
            onClick={handleUploadAll}
            disabled={uploading || hasIncomplete}
            loading={uploading}
          >
            <BiCheck size={20} />
            {DOCS_IMPORT.UPLOAD_FILES.UPLOAD_ALL}
          </Button>
        )}
        {allUploaded && (
          <Button type="primary" onClick={handleContinueToLink}>
            <BiCheck size={20} />
            {DOCS_IMPORT.UPLOAD_FILES.CONTINUE}
          </Button>
        )}
      </div>
    );

    const typeLabel = (type) => get(DOCUMENT_TYPE_LABELS, type, type);

    return (
      <StepCard
        title={DOCS_IMPORT.UPLOAD_FILES.TITLE}
        description={DOCS_IMPORT.UPLOAD_FILES.DESCRIPTION}
        actions={uploadActions}
      >
        {existingAttachmentsLoading ? (
          <div className="d-flex justify--center p-24">
            <Spin />
          </div>
        ) : (
          hasExistingDocs && (
            <div className="mb-24">
              <h4 className="pricers-import__section-title">
                {DOCS_IMPORT.UPLOAD_FILES.EXISTING_TITLE}
              </h4>
              <p className="pricers-import__section-desc mb-12">
                {DOCS_IMPORT.UPLOAD_FILES.EXISTING_DESCRIPTION}
              </p>
              <div className="mb-12 w-100-100">
                <Input
                  value={existingDocsSearch}
                  onChange={(e) => {
                    setExistingDocsSearch(e.target.value);
                    setExistingDocsPage(1);
                  }}
                  placeholder={
                    DOCS_IMPORT.UPLOAD_FILES.EXISTING_SEARCH_PLACEHOLDER
                  }
                  className="w-100-100"
                />
              </div>
              {filteredExistingAttachments.length === 0 ? (
                <p className="pricers-import__section-desc mb-0">
                  {DOCS_IMPORT.UPLOAD_FILES.EXISTING_NO_RESULTS}
                </p>
              ) : (
                <>
                  <table className="pricers-table">
                    <thead>
                      <tr className="pricers-table__header-row">
                        <th className="pricers-table__th">
                          {DOCS_IMPORT.UPLOAD_FILES.COL_FILENAME}
                        </th>
                        <th className="pricers-table__th">
                          {DOCS_IMPORT.UPLOAD_FILES.COL_TYPE}
                        </th>
                        <th className="pricers-table__th" />
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedExistingAttachments.map((att) => (
                        <tr
                          key={att.id}
                          className="pricers-table__row pricers-table__row--hoverable"
                        >
                          <td className="pricers-table__td">
                            <div className="d-flex align--center gap-8">
                              <MdOutlineInsertDriveFile
                                size={18}
                                color={COLORS.C_SUCCESS}
                              />
                              <span className="pricers-import__field-label">
                                {att.name || att.filename}
                              </span>
                            </div>
                          </td>
                          <td className="pricers-table__td">
                            <span className="pricers-import__sample-tag">
                              {typeLabel(att.type)}
                            </span>
                          </td>
                          <td className="pricers-table__td">
                            <MdClose
                              size={18}
                              color={COLORS.C_GRAY}
                              className="pricers-import__clickable"
                              onClick={() =>
                                handleDeleteExistingAttachment(att.id)
                              }
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredExistingAttachments.length >
                    EXISTING_ATTACHMENTS_PAGE_SIZE && (
                    <div className="d-flex justify--center mt-16">
                      <Pagination
                        current={existingDocsPage}
                        pageSize={EXISTING_ATTACHMENTS_PAGE_SIZE}
                        total={filteredExistingAttachments.length}
                        onChange={(page) => setExistingDocsPage(page)}
                        showSizeChanger={false}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          )
        )}

        <h4 className="pricers-import__section-title mb-8">
          {DOCS_IMPORT.UPLOAD_FILES.NEW_UPLOAD_TITLE}
        </h4>

        <div className="d-flex align--center gap-12 mb-12">
          <span className="pricers-import__field-label">
            {DOCS_IMPORT.UPLOAD_FILES.DEFAULT_TYPE}
          </span>
          <Select
            options={typeOptions}
            value={defaultDocType || undefined}
            placeholder={DOCS_IMPORT.UPLOAD_FILES.SELECT_TYPE}
            onChange={handleDefaultDocTypeChange}
            allowClear
            className="pricers-import__mapping-select"
          />
        </div>

        <div
          onDrop={handleDrop}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onClick={() => pdfInputRef.current && pdfInputRef.current.click()}
          className={classNames("pricers-import__dropzone", {
            "pricers-import__dropzone--dragover":
              dragOver && pendingFiles.length === 0,
            "pricers-import__dropzone--selected": pendingFiles.length > 0,
          })}
        >
          {pendingFiles.length > 0 ? (
            <div>
              <MdOutlineInsertDriveFile size={48} color={COLORS.C_SUCCESS} />
              <p className="pricers-import__dropzone-filename">
                {pendingFiles.length} {DOCS_IMPORT.UPLOAD_FILES.FILES_SELECTED}
              </p>
            </div>
          ) : (
            <div>
              <MdOutlineCloudUpload size={48} color={COLORS.C_GRAY} />
              <p className="pricers-import__dropzone-text">
                {DOCS_IMPORT.UPLOAD_FILES.DROP_TEXT}
              </p>
              <p className="pricers-import__dropzone-hint">
                {DOCS_IMPORT.UPLOAD_FILES.FORMATS}
              </p>
              <p className="pricers-import__dropzone-hint">
                {DOCS_IMPORT.UPLOAD_FILES.MAX_SIZE.replace(
                  "{{size}}",
                  MAX_PDF_SIZE_MB,
                )}
              </p>
            </div>
          )}
        </div>
        <input
          ref={pdfInputRef}
          type="file"
          accept={PDF_ACCEPT_STRING}
          multiple
          className="hidden-input"
          onChange={(e) => {
            handlePdfFiles(e.target.files);
            e.target.value = "";
          }}
        />

        {pendingFiles.length > 0 && (
          <table className="pricers-table mt-16">
            <thead>
              <tr className="pricers-table__header-row">
                <th className="pricers-table__th">
                  {DOCS_IMPORT.UPLOAD_FILES.COL_FILENAME}
                </th>
                <th className="pricers-table__th">
                  {DOCS_IMPORT.UPLOAD_FILES.COL_TYPE}
                </th>
                <th className="pricers-table__th" />
              </tr>
            </thead>
            <tbody>
              {pendingFiles.map((pf) => (
                <tr key={pf.id} className="pricers-table__row">
                  <td className="pricers-table__td">
                    <Input
                      value={pf.name}
                      onChange={(e) =>
                        handlePdfNameChange(pf.id, e.target.value)
                      }
                      disabled={pf.status === "uploaded"}
                      errors={
                        !pf.name.trim()
                          ? DOCS_IMPORT.UPLOAD_FILES.NAME_REQUIRED
                          : undefined
                      }
                      touched={!pf.name.trim()}
                    />
                  </td>
                  <td className="pricers-table__td">
                    <Select
                      options={typeOptions}
                      value={pf.type || undefined}
                      placeholder={DOCS_IMPORT.UPLOAD_FILES.SELECT_TYPE}
                      onChange={(val) => handlePdfTypeChange(pf.id, val)}
                      className="pricers-import__mapping-select"
                      errors={
                        !pf.type
                          ? DOCS_IMPORT.UPLOAD_FILES.TYPE_REQUIRED
                          : undefined
                      }
                      touched={!pf.type}
                    />
                  </td>
                  <td className="pricers-table__td">
                    <MdClose
                      size={18}
                      color={COLORS.C_GRAY}
                      className="pricers-import__clickable"
                      onClick={() => handleRemoveFile(pf.id)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {uploadResult && (
          <div
            className={classNames("pricers-import__status-banner mt-16", {
              "pricers-import__status-banner--success": uploadResult.success,
              "pricers-import__status-banner--error": !uploadResult.success,
            })}
          >
            {uploadResult.success ? (
              <MdOutlineCheckCircle size={24} color={COLORS.C_SUCCESS} />
            ) : (
              <MdErrorOutline size={24} color={COLORS.C_DANGER} />
            )}
            <div>
              <strong>
                {uploadResult.uploaded}{" "}
                {DOCS_IMPORT.UPLOAD_FILES.FILES_UPLOADED}
              </strong>
              {uploadResult.failed > 0 && (
                <p className="pricers-import__status-meta">
                  {uploadResult.failed} {DOCS_IMPORT.UPLOAD_FILES.FILES_FAILED}
                </p>
              )}
            </div>
          </div>
        )}
      </StepCard>
    );
  };

  const renderUploadCsv = () => {
    const handleDrop = (e) => {
      e.preventDefault();
      setCsvDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) handleCsvFile(file);
    };

    return (
      <StepCard
        title={DOCS_IMPORT.UPLOAD_CSV.TITLE}
        description={DOCS_IMPORT.UPLOAD_CSV.DESCRIPTION}
        onBack={goBack}
        backLabel={DOCS_IMPORT.BACK}
      >
        <div
          onDrop={handleDrop}
          onDragOver={(e) => {
            e.preventDefault();
            setCsvDragOver(true);
          }}
          onDragLeave={() => setCsvDragOver(false)}
          onClick={() => csvInputRef.current && csvInputRef.current.click()}
          className={classNames("pricers-import__dropzone", {
            "pricers-import__dropzone--dragover":
              csvDragOver && !selectedCsvFile,
            "pricers-import__dropzone--selected": !!selectedCsvFile,
          })}
        >
          {validating ? (
            <div>
              <Spin size="large" />
              <p className="pricers-import__dropzone-loading">
                {DOCS_IMPORT.UPLOAD_CSV.ANALYZING}
              </p>
            </div>
          ) : selectedCsvFile ? (
            <div>
              <MdOutlineInsertDriveFile size={48} color={COLORS.C_SUCCESS} />
              <p className="pricers-import__dropzone-filename">
                {selectedCsvFile.name}
              </p>
              <p className="pricers-import__dropzone-filesize">
                {(selectedCsvFile.size / 1024).toFixed(1)} KB
              </p>
            </div>
          ) : (
            <div>
              <MdOutlineCloudUpload size={48} color={COLORS.C_GRAY} />
              <p className="pricers-import__dropzone-text">
                {DOCS_IMPORT.UPLOAD_CSV.DROP_TEXT}
              </p>
              <p className="pricers-import__dropzone-hint">
                {DOCS_IMPORT.UPLOAD_CSV.FORMATS}
              </p>
              <p className="pricers-import__dropzone-hint">
                {DOCS_IMPORT.UPLOAD_CSV.MAX_SIZE.replace(
                  "{{size}}",
                  MAX_FILE_SIZE_MB,
                )}
              </p>
            </div>
          )}
        </div>
        {sizeError && (
          <div className="pricers-import__upload-error">
            <MdOutlineError size={18} />
            {DOCS_IMPORT.UPLOAD_CSV.FILE_TOO_LARGE.replace(
              "{{size}}",
              MAX_FILE_SIZE_MB,
            )}
          </div>
        )}
        <input
          ref={csvInputRef}
          type="file"
          accept={FILE_ACCEPT_STRING}
          className="hidden-input"
          onChange={(e) => {
            const file = e.target.files[0];
            if (file) handleCsvFile(file);
          }}
        />
        <div className="pricers-import__template-section">
          <div className="pricers-import__template-hint">
            <p>{DOCS_IMPORT.UPLOAD_CSV.TEMPLATE_HINT}</p>
            <p>{DOCS_IMPORT.UPLOAD_CSV.REQUIRED_HINT}</p>
          </div>
          <button
            type="button"
            className="pricers-import__template-btn"
            onClick={downloadTemplate}
          >
            <MdOutlineDownload size={18} />
            {DOCS_IMPORT.UPLOAD_CSV.DOWNLOAD_TEMPLATE}
          </button>
        </div>
      </StepCard>
    );
  };

  const renderMapping = () => {
    const fields = get(validationResult, "fields", []);
    const headers = get(validationResult, "headers", []);
    const columnSamples = get(validationResult, "columnSamples", {});
    const FIELD_LABELS = DOCS_IMPORT.MAPPING.FIELDS;

    const usedColumns = new Set(Object.values(mapping).filter(Boolean));
    const requiredFields = fields.filter((f) => f.required);
    const mappedRequired = requiredFields.filter((f) => mapping[f.key]).length;

    const handleFieldChange = (fieldKey, columnName) => {
      setMapping({
        ...mapping,
        [fieldKey]: columnName || undefined,
      });
    };

    const handleClear = (fieldKey) => {
      const next = { ...mapping };
      delete next[fieldKey];
      setMapping(next);
    };

    const columnOptions = headers.map((h) => ({
      label: usedColumns.has(h) ? `${h} (${DOCS_IMPORT.MAPPING.USED})` : h,
      value: h,
      disabled:
        usedColumns.has(h) && !Object.entries(mapping).some(([, v]) => v === h),
    }));

    const confirmButton = (
      <Button
        type="primary"
        onClick={() => handleMappingConfirm(mapping)}
        disabled={mappedRequired < requiredFields.length || validating}
        loading={validating}
      >
        <BiCheck size={20} />
        {DOCS_IMPORT.MAPPING.CONFIRM}
      </Button>
    );

    return (
      <StepCard
        title={DOCS_IMPORT.MAPPING.TITLE}
        description={DOCS_IMPORT.MAPPING.DESCRIPTION}
        onBack={goBack}
        backLabel={DOCS_IMPORT.BACK_CHANGE_FILE}
        actions={confirmButton}
      >
        <div className="pricers-import__progress-bar">
          <span className="pricers-import__progress-label">
            {mappedRequired}/{requiredFields.length}{" "}
            {DOCS_IMPORT.MAPPING.REQUIRED_MAPPED}
          </span>
          <div className="pricers-import__progress-track">
            <div
              className={classNames("pricers-import__progress-fill", {
                "pricers-import__progress-fill--complete":
                  mappedRequired === requiredFields.length,
              })}
              style={{
                width: `${(mappedRequired / requiredFields.length) * 100}%`,
              }}
            />
          </div>
        </div>

        <table className="pricers-table">
          <thead>
            <tr className="pricers-table__header-row">
              <th className="pricers-table__th">
                {DOCS_IMPORT.MAPPING.EXPECTED_FIELD}
              </th>
              <th className="pricers-table__th">
                {DOCS_IMPORT.MAPPING.FILE_COLUMN}
              </th>
              <th className="pricers-table__th">
                {DOCS_IMPORT.MAPPING.PREVIEW}
              </th>
            </tr>
          </thead>
          <tbody>
            {fields.map((field) => {
              const translationKey = field.key.toUpperCase();
              const isMapped = !!mapping[field.key];
              const isRequired = field.required;

              return (
                <tr key={field.key} className="pricers-table__row">
                  <td className="pricers-table__td">
                    <div className="d-flex align--center gap-8">
                      <span
                        className={classNames("pricers-import__status-dot", {
                          "pricers-import__status-dot--mapped": isMapped,
                          "pricers-import__status-dot--required":
                            !isMapped && isRequired,
                          "pricers-import__status-dot--optional":
                            !isMapped && !isRequired,
                        })}
                      />
                      <span className="pricers-import__field-label">
                        {FIELD_LABELS[translationKey] || field.key}
                      </span>
                      {isRequired && (
                        <span className="pricers-import__required-badge">
                          {DOCS_IMPORT.MAPPING.REQUIRED}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="pricers-table__td">
                    <div className="d-flex align--center gap-8">
                      <Select
                        showSearch
                        options={columnOptions}
                        value={mapping[field.key] || undefined}
                        placeholder={DOCS_IMPORT.MAPPING.SELECT_COLUMN}
                        onChange={(val) => handleFieldChange(field.key, val)}
                        allowClear
                        className="pricers-import__mapping-select"
                      />
                      {isMapped && (
                        <MdClose
                          size={18}
                          color={COLORS.C_GRAY}
                          className="pricers-import__clickable"
                          onClick={() => handleClear(field.key)}
                        />
                      )}
                    </div>
                  </td>
                  <td className="pricers-table__td">
                    <div className="d-flex gap-4 flex--wrap">
                      {(columnSamples[mapping[field.key]] || [])
                        .slice(0, 4)
                        .map((val, i) => (
                          <span key={i} className="pricers-import__sample-tag">
                            {formatDocsSampleValue(val, field.key)}
                          </span>
                        ))}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </StepCard>
    );
  };

  const renderValidation = () => {
    const isValid = get(validationResult, "valid", false);
    const errors = get(validationResult, "errors", []);
    const warnings = get(validationResult, "warnings", []);
    const preview = get(validationResult, "preview", {});
    const totalRows = get(validationResult, "totalRows", 0);
    const plans = get(preview, "plans", []);
    const allFilenames = get(preview, "allFilenames", []);

    let totalDocs = 0;
    for (const plan of plans) {
      totalDocs += plan.documentCount || 0;
    }

    const nextButton = (
      <Button type="primary" onClick={handleValidationNext} disabled={!isValid}>
        <BiCheck size={20} />
        {DOCS_IMPORT.VALIDATION.NEXT}
      </Button>
    );

    return (
      <StepCard
        title={DOCS_IMPORT.VALIDATION.TITLE}
        onBack={goBack}
        backLabel={DOCS_IMPORT.BACK_EDIT_MAPPING}
        actions={nextButton}
      >
        <div
          className={classNames("pricers-import__status-banner", {
            "pricers-import__status-banner--success": isValid,
            "pricers-import__status-banner--error": !isValid,
          })}
        >
          {isValid ? (
            <MdOutlineCheckCircle size={24} color={COLORS.C_SUCCESS} />
          ) : (
            <MdErrorOutline size={24} color={COLORS.C_DANGER} />
          )}
          <div>
            <strong>
              {isValid
                ? DOCS_IMPORT.VALIDATION.SUCCESS
                : `${errors.length} ${DOCS_IMPORT.VALIDATION.ERRORS_FOUND}`}
            </strong>
            <p className="pricers-import__status-meta">
              {totalRows} {DOCS_IMPORT.VALIDATION.ROWS_ANALYZED}
            </p>
          </div>
        </div>

        {errors.length > 0 && (
          <div className="pricers-import__error-section">
            <h4 className="pricers-import__error-title">
              {DOCS_IMPORT.VALIDATION.ERROR_DETAILS}
            </h4>
            <div className="pricers-import__error-list">
              {errors.slice(0, 30).map((err, i) => (
                <div key={i} className="pricers-import__error-item">
                  <MdErrorOutline size={16} />
                  {resolveError(err, DOCS_IMPORT.ERROR_KEYS)}
                </div>
              ))}
            </div>
          </div>
        )}

        {warnings.length > 0 && (
          <div className="mb-24">
            {warnings.map((warn, i) => (
              <div key={i} className="pricers-import__warning-item">
                {typeof warn === "string" ? warn : JSON.stringify(warn)}
              </div>
            ))}
          </div>
        )}

        <div className="grid--3 gap-16 mb-24">
          <div className="pricers-import__stat-card">
            <MdOutlineCategory
              size={22}
              className="pricers-import__stat-icon"
            />
            <div className="pricers-import__stat-value">{plans.length}</div>
            <div className="pricers-import__stat-label">
              {DOCS_IMPORT.VALIDATION.PLANS_DETECTED}
            </div>
          </div>
          <div className="pricers-import__stat-card">
            <MdOutlineInsertDriveFile
              size={22}
              className="pricers-import__stat-icon"
            />
            <div className="pricers-import__stat-value">{totalDocs}</div>
            <div className="pricers-import__stat-label">
              {DOCS_IMPORT.VALIDATION.DOCUMENTS_DETECTED}
            </div>
          </div>
          <div className="pricers-import__stat-card">
            <MdOutlineInsertDriveFile
              size={22}
              className="pricers-import__stat-icon"
            />
            <div className="pricers-import__stat-value">
              {allFilenames.length}
            </div>
            <div className="pricers-import__stat-label">
              {DOCS_IMPORT.VALIDATION.UNIQUE_FILES}
            </div>
          </div>
        </div>

        {plans.length > 0 && (
          <div className="pricers-import__product-list">
            <div className="pricers-import__product-grid">
              {plans.slice(0, 50).map((p, i) => (
                <div key={i} className="pricers-import__product-item">
                  <div className="pricers-import__product-name">
                    {p.identifier}
                  </div>
                  <div className="pricers-import__product-meta">
                    <span className="pricers-import__product-tag">
                      {p.documentCount} doc(s)
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </StepCard>
    );
  };

  const renderAnalysis = () => {
    const preview = get(validationResult, "preview", {});
    const existingPlans = get(preview, "existingPlans", 0);
    const notFoundPlans = get(preview, "notFoundPlans", 0);
    const totalRows = get(validationResult, "totalRows", 0);

    const launchButton = (
      <Button type="primary" onClick={handleExecuteLink} disabled={!confirmed}>
        <BiCheck size={20} />
        {DOCS_IMPORT.ANALYSIS.LAUNCH_IMPORT}
      </Button>
    );

    return (
      <StepCard
        title={DOCS_IMPORT.ANALYSIS.TITLE}
        description={DOCS_IMPORT.ANALYSIS.DESCRIPTION}
        onBack={goBack}
        backLabel={DOCS_IMPORT.BACK_REVIEW_VALIDATION}
        actions={launchButton}
      >
        <div className="grid--3 gap-16 mb-24">
          <div className="pricers-import__analysis-stat pricers-import__analysis-stat--total">
            <div className="pricers-import__analysis-value pricers-import__analysis-value--total">
              {totalRows}
            </div>
            <div className="pricers-import__analysis-label pricers-import__analysis-label--muted">
              {DOCS_IMPORT.ANALYSIS.TOTAL_ROWS}
            </div>
          </div>
          <div className="pricers-import__analysis-stat pricers-import__analysis-stat--existing">
            <div className="d-flex align--center justify--center gap-6">
              <MdOutlineCheckCircle size={22} color={COLORS.C_SUCCESS} />
              <span className="pricers-import__analysis-value pricers-import__analysis-value--existing">
                {existingPlans}
              </span>
            </div>
            <div className="pricers-import__analysis-label pricers-import__analysis-label--existing">
              {DOCS_IMPORT.ANALYSIS.PLANS_FOUND}
            </div>
            <div className="pricers-import__analysis-sublabel">
              {DOCS_IMPORT.ANALYSIS.PLANS_FOUND_DESC}
            </div>
          </div>
          <div className="pricers-import__analysis-stat pricers-import__analysis-stat--new">
            {notFoundPlans > 0 ? (
              <div className="d-flex align--center justify--center gap-6">
                <MdOutlineWarning size={22} color={COLORS.C_DANGER} />
                <span className="pricers-import__analysis-value pricers-import__analysis-value--new">
                  {notFoundPlans}
                </span>
              </div>
            ) : (
              <div className="pricers-import__analysis-value pricers-import__analysis-value--new">
                {notFoundPlans}
              </div>
            )}
            <div className="pricers-import__analysis-label pricers-import__analysis-label--new">
              {DOCS_IMPORT.ANALYSIS.PLANS_NOT_FOUND}
            </div>
            <div className="pricers-import__analysis-sublabel">
              {DOCS_IMPORT.ANALYSIS.PLANS_NOT_FOUND_DESC}
            </div>
          </div>
        </div>

        <div className="pricers-import__confirm-banner pricers-import__confirm-banner--warning">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            className="pricers-checkbox pricers-checkbox--lg"
          />
          <span className="pricers-import__confirm-label">
            {DOCS_IMPORT.ANALYSIS.CONFIRM_ALL}
          </span>
        </div>
      </StepCard>
    );
  };

  const renderLinking = () => (
    <StepCard>
      <div className="pricers-import__loading-container">
        <div className="pricers-import__loading-icon">
          <Spin size="large" />
        </div>
        <h2>{DOCS_IMPORT.LINKING.TITLE}</h2>
        <p className="pricers-import__loading-description">
          {DOCS_IMPORT.LINKING.DESCRIPTION}
        </p>
        <p className="pricers-import__loading-warning">
          {DOCS_IMPORT.LINKING.WARNING}
        </p>
      </div>
    </StepCard>
  );

  const renderResult = () => {
    const result = linkResult || {
      success: false,
      errors: [{ key: "UNEXPECTED_ERROR", params: { message: "" } }],
    };
    const success = get(result, "success", false);
    const errors = get(result, "errors", []);
    const updated = get(result, "updated", 0);
    const skipped = get(result, "skipped", 0);
    const totalRows = get(result, "totalRows", 0);

    const notFoundFiles = errors.filter(
      (e) => get(e, "key") === "ATTACHMENT_NOT_FOUND",
    );
    const otherErrors = errors.filter(
      (e) => get(e, "key") !== "ATTACHMENT_NOT_FOUND",
    );
    const hasNotFound = notFoundFiles.length > 0;
    const isPartial = success && hasNotFound;

    const resultTitle = !success
      ? DOCS_IMPORT.RESULT.FAILURE_TITLE
      : isPartial
        ? DOCS_IMPORT.RESULT.PARTIAL_TITLE
        : DOCS_IMPORT.RESULT.SUCCESS_TITLE;

    const iconColor = !success
      ? COLORS.C_DANGER
      : isPartial
        ? COLORS.C_WARNING
        : COLORS.C_SUCCESS;

    const resultActions = (
      <>
        <Button type="primary--outlined" onClick={handleReset}>
          <MdOutlineRefresh size={20} />
          {DOCS_IMPORT.RESULT.IMPORT_ANOTHER}
        </Button>
        <Button type="primary" onClick={handleFinish}>
          <MdOutlineExplore size={20} />
          {DOCS_IMPORT.RESULT.VIEW_PLANS}
        </Button>
      </>
    );

    return (
      <StepCard actions={resultActions}>
        <div className="pricers-import__result-container">
          <div
            className={classNames("pricers-import__result-icon", {
              "pricers-import__result-icon--success": success && !isPartial,
              "pricers-import__result-icon--failure": !success,
            })}
          >
            {!success ? (
              <MdOutlineError size={48} color={iconColor} />
            ) : isPartial ? (
              <MdOutlineWarning size={48} color={iconColor} />
            ) : (
              <MdOutlineCheckCircle size={48} color={iconColor} />
            )}
          </div>
          <h2>{resultTitle}</h2>
        </div>

        <div className="grid--2 gap-16 mb-16">
          <div className="pricers-import__result-stat pricers-import__result-stat--updated">
            <div className="pricers-import__result-stat-value pricers-import__result-stat-value--updated">
              {updated}
            </div>
            <div className="pricers-import__stat-label">
              {DOCS_IMPORT.RESULT.UPDATED}
            </div>
          </div>
          <div className="pricers-import__result-stat pricers-import__result-stat--skipped">
            <div className="pricers-import__result-stat-value pricers-import__result-stat-value--skipped">
              {skipped}
            </div>
            <div className="pricers-import__stat-label">
              {DOCS_IMPORT.RESULT.SKIPPED}
            </div>
          </div>
        </div>

        <div className="pricers-import__result-footer mb-16">
          <span>
            {totalRows} {DOCS_IMPORT.RESULT.ROWS_PROCESSED}
          </span>
        </div>

        {hasNotFound && (
          <div className="pricers-import__error-section mb-16">
            <h4 className="pricers-import__error-title">
              <MdErrorOutline size={18} />
              {DOCS_IMPORT.RESULT.NOT_FOUND_FILES_TITLE}
              {" ("}
              {notFoundFiles.length}
              {")"}
            </h4>
            <table className="pricers-import__table">
              <thead>
                <tr>
                  <th>{DOCS_IMPORT.RESULT.COL_LINE}</th>
                  <th>{DOCS_IMPORT.RESULT.COL_FILENAME}</th>
                  <th>{DOCS_IMPORT.RESULT.COL_TYPE}</th>
                </tr>
              </thead>
              <tbody>
                {notFoundFiles.map((err, i) => (
                  <tr key={i}>
                    <td>{get(err, "params.line", "")}</td>
                    <td>{get(err, "params.filename", "")}</td>
                    <td>
                      {get(
                        DOCUMENT_TYPE_LABELS,
                        get(err, "params.type", ""),
                        get(err, "params.type", ""),
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {otherErrors.length > 0 && (
          <div className="pricers-import__error-section">
            <h4 className="pricers-import__error-title">
              {DOCS_IMPORT.RESULT.ERROR_DETAILS}
            </h4>
            <div className="pricers-import__error-list">
              {otherErrors.map((err, i) => (
                <div key={i} className="pricers-import__error-item">
                  {resolveError(err, DOCS_IMPORT.ERROR_KEYS)}
                </div>
              ))}
            </div>
          </div>
        )}
      </StepCard>
    );
  };

  return (
    <>
      <Header
        title={DOCS_IMPORT.TITLE}
        subtitle={DOCS_IMPORT.SUBTITLE}
        actions={
          stepName === DOCS_IMPORT_STEPS.UPLOAD_FILES
            ? [
                {
                  text: DOCS_IMPORT.BACK,
                  onClick: goBack,
                  type: "primary--outlined",
                  icon: <BiChevronLeft size={20} />,
                },
              ]
            : []
        }
      />
      <div className="pricers-import__stepper">
        {stepLabels.map((label, idx) => {
          const isActive = idx === visibleStepIndex;
          const isDone = idx < visibleStepIndex;
          return (
            <div key={idx} className="pricers-import__step-item">
              <div className="pricers-import__step-row">
                <div
                  className={classNames("pricers-import__step-connector", {
                    "pricers-import__step-connector--done": isDone || isActive,
                    "pricers-import__step-connector--hidden": idx === 0,
                  })}
                />
                <span
                  className={classNames("pricers-import__step-number", {
                    "pricers-import__step-number--active": isActive,
                    "pricers-import__step-number--done": isDone,
                  })}
                >
                  {isDone ? "\u2713" : idx + 1}
                </span>
                <div
                  className={classNames("pricers-import__step-connector", {
                    "pricers-import__step-connector--done": isDone,
                    "pricers-import__step-connector--hidden":
                      idx === stepLabels.length - 1,
                  })}
                />
              </div>
              <span
                className={classNames("pricers-import__step-label", {
                  "pricers-import__step-label--active": isActive,
                  "pricers-import__step-label--done": isDone,
                })}
              >
                {label}
              </span>
            </div>
          );
        })}
      </div>

      {stepName === DOCS_IMPORT_STEPS.UPLOAD_FILES && renderUploadFiles()}
      {stepName === DOCS_IMPORT_STEPS.UPLOAD_CSV && renderUploadCsv()}
      {stepName === DOCS_IMPORT_STEPS.MAPPING &&
        validationResult &&
        renderMapping()}
      {stepName === DOCS_IMPORT_STEPS.VALIDATION &&
        validationResult &&
        renderValidation()}
      {stepName === DOCS_IMPORT_STEPS.ANALYSIS &&
        validationResult &&
        renderAnalysis()}
      {stepName === DOCS_IMPORT_STEPS.LINKING && renderLinking()}
      {stepName === DOCS_IMPORT_STEPS.RESULT && renderResult()}
    </>
  );
};

export default DocsImportWizard;
